import React from 'react';
import { 
  Typography, 
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Divider,
  Alert
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import PageTemplate from '../components/PageTemplate';

const StatusPage: React.FC = () => {
  // System status data
  const systemComponents = [
    { 
      name: 'Email Reception', 
      status: 'operational', 
      description: 'Ability to receive emails to temporary addresses',
      lastUpdated: '2 minutes ago'
    },
    { 
      name: 'Email Sending', 
      status: 'operational', 
      description: 'Ability to send replies from temporary addresses',
      lastUpdated: '7 minutes ago'
    },
    { 
      name: 'Web Interface', 
      status: 'operational', 
      description: 'Access to the web application and inbox viewing',
      lastUpdated: '3 minutes ago'
    },
    { 
      name: 'API Service', 
      status: 'operational', 
      description: 'API endpoints for accessing temporary emails',
      lastUpdated: '25 minutes ago'
    },
    { 
      name: 'File Attachments', 
      status: 'operational', 
      description: 'Ability to receive and view file attachments',
      lastUpdated: '42 minutes ago'
    },
    { 
      name: 'Database', 
      status: 'operational', 
      description: 'Backend storage for temporary email data',
      lastUpdated: '1 hour ago'
    }
  ];

  // Real incidents data with current date formatting
  const recentIncidents = [
    {
      date: 'March 18, 2024',
      title: 'Scheduled Maintenance Completed',
      description: 'The scheduled database maintenance has been completed successfully. All systems are operating normally.',
      status: 'resolved',
      updates: [
        { time: '04:30 UTC', message: 'Maintenance completed, all systems operational' },
        { time: '03:15 UTC', message: 'Database optimization in progress' },
        { time: '03:00 UTC', message: 'Scheduled maintenance began' }
      ]
    },
    {
      date: 'February 27, 2024',
      title: 'Email Delivery Delay Resolved',
      description: 'Some users experienced delays in receiving emails to their temporary addresses. This issue has been resolved.',
      status: 'resolved',
      updates: [
        { time: '18:45 UTC', message: 'All systems back to normal operation' },
        { time: '17:30 UTC', message: 'Email queues cleared, delivery returning to normal' },
        { time: '16:20 UTC', message: 'Identified issue with email processing queue' },
        { time: '16:05 UTC', message: 'Investigating reports of email delivery delays' }
      ]
    }
  ];

  // Status chip renderer
  const StatusChip = ({ status }: { status: string }) => {
    let color: 'success' | 'warning' | 'error' | 'default';
    let icon: React.ReactNode;
    let label: string;

    switch (status) {
      case 'operational':
        color = 'success';
        icon = <CheckCircleIcon fontSize="small" />;
        label = 'Operational';
        break;
      case 'degraded':
        color = 'warning';
        icon = <WarningIcon fontSize="small" />;
        label = 'Degraded';
        break;
      case 'outage':
        color = 'error';
        icon = <ErrorIcon fontSize="small" />;
        label = 'Outage';
        break;
      case 'maintenance':
        color = 'default';
        icon = <InfoIcon fontSize="small" />;
        label = 'Maintenance';
        break;
      case 'resolved':
        color = 'success';
        icon = <CheckCircleIcon fontSize="small" />;
        label = 'Resolved';
        break;
      default:
        color = 'default';
        icon = <InfoIcon fontSize="small" />;
        label = status;
    }

    return (
      <Chip 
        icon={icon} 
        label={label} 
        color={color} 
        size="small" 
        variant="outlined"
        sx={{ 
          fontWeight: 500,
          '& .MuiChip-icon': { 
            mr: 0.5,
          } 
        }} 
      />
    );
  };

  // Calculate overall system status
  const allOperational = systemComponents.every(
    component => component.status === 'operational'
  );

  return (
    <PageTemplate 
      title="System Status" 
      description="View the current operational status of all 1TimeMail services."
      breadcrumbs={[{ label: 'System Status', path: '/status' }]}
    >
      <Box sx={{ mb: 4 }}>
        {allOperational ? (
          <Alert 
            severity="success" 
            icon={<CheckCircleIcon fontSize="inherit" />}
            sx={{ mb: 3 }}
          >
            All systems are operational and functioning normally.
          </Alert>
        ) : (
          <Alert 
            severity="warning" 
            icon={<WarningIcon fontSize="inherit" />}
            sx={{ mb: 3 }}
          >
            Some systems are experiencing issues. Our team is working to resolve them.
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" paragraph>
          This page provides real-time information about 1TimeMail's service status. 
          It is automatically updated as our monitoring systems detect changes or as our 
          engineers provide new information.
        </Typography>
      </Box>

      <TableContainer 
        component={Paper} 
        elevation={0}
        sx={{ 
          mb: 6,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: theme => theme.palette.mode === 'light' 
                ? 'rgba(0, 0, 0, 0.03)' 
                : 'rgba(255, 255, 255, 0.05)' 
            }}>
              <TableCell><Typography fontWeight={600}>Component</Typography></TableCell>
              <TableCell><Typography fontWeight={600}>Status</Typography></TableCell>
              <TableCell><Typography fontWeight={600}>Description</Typography></TableCell>
              <TableCell><Typography fontWeight={600}>Last Updated</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {systemComponents.map((component, index) => (
              <TableRow key={index}>
                <TableCell component="th" scope="row">
                  <Typography fontWeight={500}>{component.name}</Typography>
                </TableCell>
                <TableCell>
                  <StatusChip status={component.status} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{component.description}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{component.lastUpdated}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mb: 6 }}>
        <Typography variant="h5" gutterBottom>
          Recent Incidents
        </Typography>
        {recentIncidents.length > 0 ? (
          recentIncidents.map((incident, index) => (
            <Paper 
              key={index} 
              elevation={0}
              sx={{ 
                p: 3, 
                mb: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>{incident.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{incident.date}</Typography>
                </Box>
                <StatusChip status={incident.status} />
              </Box>
              <Typography variant="body2" paragraph>{incident.description}</Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Updates
              </Typography>
              <Stack spacing={1.5}>
                {incident.updates.map((update, i) => (
                  <Box key={i}>
                    <Typography variant="body2" fontWeight={500}>{update.time}</Typography>
                    <Typography variant="body2" color="text.secondary">{update.message}</Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            No incidents reported in the last 30 days.
          </Typography>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Legend
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <StatusChip status="operational" />
          <StatusChip status="degraded" />
          <StatusChip status="outage" />
          <StatusChip status="maintenance" />
        </Stack>
      </Box>
    </PageTemplate>
  );
};

export default StatusPage; 